if (!customElements.get('wholesale-process')) {
  class WholesaleProcess extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-wholesale-step]'));
      this.stepsContainer = this.querySelector('[data-wholesale-steps]');
      this.currentNumber = this.querySelector('[data-wholesale-current]');
      this.currentTitle = this.querySelector('[data-wholesale-current-title]');
      this.previousButton = this.querySelector('[data-wholesale-prev]');
      this.nextButton = this.querySelector('[data-wholesale-next]');
      this.dots = Array.from(this.querySelectorAll('[data-wholesale-dot]'));
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currentIndex = 0;

      if (!this.steps.length || !this.stepsContainer) return;

      this.setupInfiniteCarousel();

      if (this.reduceMotion) {
        this.steps.forEach((step) => step.classList.add('is-visible'));
      } else {
        this.classList.add('wholesale-flow--motion-ready');
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            if (!this.mobileQuery.matches) this.setCurrentStep(entry.target);
          });
        }, { rootMargin: '-8% 0px -12% 0px', threshold: 0.18 });

        this.steps.forEach((step) => this.observer.observe(step));
      }

      this.previousButton?.addEventListener('click', () => this.goToStep(this.currentIndex - 1, true));
      this.nextButton?.addEventListener('click', () => this.goToStep(this.currentIndex + 1, true));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goToStep(index, true)));

      this.stepsContainer.addEventListener('scroll', () => this.onCarouselScroll(), { passive: true });
      this.stepsContainer.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stepsContainer.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false });
      this.stepsContainer.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stepsContainer.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.stepsContainer.addEventListener('focusin', () => this.stopAutoplay());
      this.stepsContainer.addEventListener('focusout', () => this.startAutoplay());

      this.onViewportChange = () => {
        window.clearTimeout(this.scrollTimer);
        if (this.mobileQuery.matches) {
          this.steps.forEach((step) => step.classList.add('is-visible'));
          this.jumpToStep(this.steps[this.currentIndex]);
          this.startAutoplay();
        } else {
          this.stopAutoplay();
          this.stepsContainer.scrollLeft = 0;
        }
      };
      this.mobileQuery.addEventListener?.('change', this.onViewportChange);
      this.visibilityHandler = () => document.hidden ? this.stopAutoplay() : this.startAutoplay();
      document.addEventListener('visibilitychange', this.visibilityHandler);

      this.setCurrentStep(this.steps[0]);
      if (this.mobileQuery.matches) {
        requestAnimationFrame(() => {
          this.jumpToStep(this.steps[0]);
          this.startAutoplay();
        });
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.stopAutoplay();
      window.clearTimeout(this.scrollTimer);
      this.mobileQuery?.removeEventListener?.('change', this.onViewportChange);
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    setupInfiniteCarousel() {
      if (this.steps.length < 2) {
        this.carouselItems = this.steps;
        return;
      }

      this.firstClone = this.steps[0].cloneNode(true);
      this.lastClone = this.steps[this.steps.length - 1].cloneNode(true);

      [this.firstClone, this.lastClone].forEach((clone) => {
        clone.classList.add('wholesale-flow__step--clone');
        clone.classList.remove('is-current');
        clone.removeAttribute('data-wholesale-step');
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('id');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      });

      this.firstClone.dataset.wholesaleClone = 'first';
      this.lastClone.dataset.wholesaleClone = 'last';
      this.stepsContainer.insertBefore(this.lastClone, this.stepsContainer.firstChild);
      this.stepsContainer.appendChild(this.firstClone);
      this.carouselItems = [this.lastClone, ...this.steps, this.firstClone];
    }

    getScrollPosition(step) {
      const containerRect = this.stepsContainer.getBoundingClientRect();
      const stepRect = step.getBoundingClientRect();
      return this.stepsContainer.scrollLeft + stepRect.left - containerRect.left;
    }

    jumpToStep(step) {
      if (!step) return;
      this.isJumping = true;
      this.stepsContainer.classList.add('is-jumping');
      this.stepsContainer.scrollLeft = this.getScrollPosition(step);
      requestAnimationFrame(() => {
        this.stepsContainer.classList.remove('is-jumping');
        this.isJumping = false;
      });
    }

    setCurrentStep(step) {
      const index = this.steps.indexOf(step);
      if (index < 0) return;
      this.currentIndex = index;
      this.steps.forEach((item) => item.classList.toggle('is-current', item === step));
      this.dots.forEach((dot, dotIndex) => {
        const current = dotIndex === index;
        dot.classList.toggle('is-current', current);
        dot.setAttribute('aria-current', String(current));
      });
      if (this.currentNumber) this.currentNumber.textContent = step.dataset.stepNumber;
      if (this.currentTitle) this.currentTitle.textContent = step.dataset.stepTitle;
    }

    goToStep(index, userInitiated = false) {
      if (!this.mobileQuery.matches || !this.steps.length) return;
      const normalizedIndex = (index + this.steps.length) % this.steps.length;
      const step = this.steps[normalizedIndex];
      let scrollTarget = step;

      if (index >= this.steps.length && this.firstClone) scrollTarget = this.firstClone;
      if (index < 0 && this.lastClone) scrollTarget = this.lastClone;

      this.setCurrentStep(step);
      this.stepsContainer.scrollTo({
        left: this.getScrollPosition(scrollTarget),
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      if (userInitiated) this.startAutoplay();
    }

    onCarouselScroll() {
      if (!this.mobileQuery.matches || this.isJumping) return;
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const containerLeft = this.stepsContainer.getBoundingClientRect().left;
        let closestStep = this.carouselItems[0];
        let closestDistance = Infinity;
        this.carouselItems.forEach((step) => {
          const distance = Math.abs(step.getBoundingClientRect().left - containerLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestStep = step;
          }
        });

        if (closestStep === this.firstClone) {
          this.setCurrentStep(this.steps[0]);
          this.jumpToStep(this.steps[0]);
        } else if (closestStep === this.lastClone) {
          this.setCurrentStep(this.steps[this.steps.length - 1]);
          this.jumpToStep(this.steps[this.steps.length - 1]);
        } else {
          this.setCurrentStep(closestStep);
        }
      }, 120);
    }

    onPointerDown(event) {
      if (!this.mobileQuery.matches || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.stopAutoplay();
      this.dragging = true;
      this.dragAxis = null;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragLastX = event.clientX;
      this.dragStartScroll = this.stepsContainer.scrollLeft;
      this.stepsContainer.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.dragLastX = event.clientX;

      if (!this.dragAxis && (Math.abs(deltaX) > 7 || Math.abs(deltaY) > 7)) {
        this.dragAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
        if (this.dragAxis === 'horizontal') this.stepsContainer.classList.add('is-dragging');
      }

      if (this.dragAxis !== 'horizontal') return;
      event.preventDefault();
      this.stepsContainer.scrollLeft = this.dragStartScroll - deltaX;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      const horizontalDrag = this.dragAxis === 'horizontal';
      const distance = this.dragLastX - this.dragStartX;
      this.dragging = false;
      this.dragAxis = null;
      this.stepsContainer.classList.remove('is-dragging');
      if (this.stepsContainer.hasPointerCapture?.(event.pointerId)) this.stepsContainer.releasePointerCapture(event.pointerId);

      if (horizontalDrag && Math.abs(distance) > 45) {
        this.goToStep(this.currentIndex + (distance < 0 ? 1 : -1), true);
      } else if (horizontalDrag) {
        this.goToStep(this.currentIndex);
      }
      if (!horizontalDrag) this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.mobileQuery.matches || this.reduceMotion || document.hidden || this.steps.length < 2) return;
      this.autoplayTimer = window.setInterval(() => this.goToStep(this.currentIndex + 1), 3000);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('wholesale-process', WholesaleProcess);
}
